import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BLOG_POSTS } from "@/data/blogs";
import { Calendar, Clock, ArrowRight, BookOpen, Search, Sparkles, Tag, ChevronDown, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function BlogListPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tümü");
  const [visibleCount, setVisibleCount] = useState(9);

  // Get list of unique categories
  const categories = useMemo(() => {
    const cats = Array.from(new Set(BLOG_POSTS.map((p) => p.category)));
    return ["Tümü", ...cats];
  }, []);

  // Filter posts by category & search term
  const filteredPosts = useMemo(() => {
    return BLOG_POSTS.filter((post) => {
      const matchesCategory = selectedCategory === "Tümü" || post.category === selectedCategory;
      const matchesSearch =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategory]);

  const featuredPost = BLOG_POSTS[0];
  const listPosts = searchTerm || selectedCategory !== "Tümü" ? filteredPosts : filteredPosts.slice(1);
  const displayedPosts = listPosts.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Adana Gayrimenkul Blog & Emlak Rehberi - Corner Gayrimenkul"
        description="Adana'da ev alımı, satımı, gayrimenkul yatırımı, tapu mevzuatı, mahalle rehberleri ve emlak haberleri. Gürselpaşa, Seyhan ve Çukurova konut piyasası ipuçları."
        canonicalPath="/blog"
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Blog & Emlak Rehberi" }]} />

        {/* Hero Banner Header */}
        <div className="mb-10 text-center max-w-4xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary font-extrabold text-xs uppercase tracking-wider">
            <BookOpen className="h-4 w-4" /> Adana Gayrimenkul Bilgi Merkezi
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight leading-tight">
            Adana Emlak & Gayrimenkul Rehberi
          </h1>
          <p className="text-muted-foreground text-base sm:text-xl max-w-2xl mx-auto leading-relaxed">
            Adana'da mülk arayışınızı planlamak için bölge rehberleri ve kontrol listeleri. Yazılarda resmî kaynak bağlantıları ve kapsam bilgileri yer alır.
          </p>

          {/* Quick Stats Badges */}
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm font-semibold text-muted-foreground">
            <span className="flex items-center gap-1.5 bg-card px-3 py-1.5 rounded-full border border-border">
              <CheckCircle2 className="h-4 w-4 text-green-500" /> 20 Detaylı Rehber Yazısı
            </span>
            <span className="flex items-center gap-1.5 bg-card px-3 py-1.5 rounded-full border border-border">
              <CheckCircle2 className="h-4 w-4 text-primary" /> Adana bölge rehberleri
            </span>
            <span className="flex items-center gap-1.5 bg-card px-3 py-1.5 rounded-full border border-border">
              <CheckCircle2 className="h-4 w-4 text-blue-500" /> Resmî kaynak bağlantıları
            </span>
          </div>
        </div>

        {/* Search & Filter Controls */}
        <div className="mb-10 space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card p-4 sm:p-6 rounded-2xl border border-border shadow-sm">
            {/* Search Input */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Konu, mahalle veya etiket ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 bg-background border-border/80 rounded-xl"
              />
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-2 overflow-x-auto w-full pb-2 sm:pb-0 scrollbar-none">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setVisibleCount(9);
                  }}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all duration-200 ${
                    selectedCategory === cat
                      ? "bg-primary text-primary-foreground shadow-md scale-105"
                      : "bg-muted hover:bg-muted/80 text-foreground/80"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Main Post (only when not searching / filtering) */}
        {!searchTerm && selectedCategory === "Tümü" && featuredPost && (
          <div className="mb-12">
            <div className="relative rounded-3xl overflow-hidden border border-border bg-card shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-0 group">
              <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden bg-muted">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-4 py-1.5 text-xs font-black rounded-full bg-primary text-primary-foreground shadow-lg flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Öne Çıkan Rehber
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5 p-6 sm:p-10 flex flex-col justify-between space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="px-3 py-1 rounded-md bg-primary/10 text-primary font-bold">{featuredPost.category}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {featuredPost.publishDate}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {featuredPost.readTime}</span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-black text-foreground group-hover:text-primary transition-colors leading-tight">
                    <Link to={`/blog/${featuredPost.slug}`}>
                      {featuredPost.title}
                    </Link>
                  </h2>

                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed line-clamp-4">
                    {featuredPost.summary}
                  </p>
                </div>

                <div className="pt-4 border-t border-border flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">Yazar: {featuredPost.author}</span>
                  <Button asChild className="font-bold rounded-xl shadow-md">
                    <Link to={`/blog/${featuredPost.slug}`}>
                      Detaylı İncele <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Posts Grid */}
        {displayedPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 my-8">
            {displayedPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300 border border-border/80 bg-card flex flex-col h-full rounded-2xl">
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-primary/90 backdrop-blur-md text-primary-foreground shadow-md">
                      {post.category}
                    </span>
                  </div>
                </div>

                <CardContent className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {post.publishDate}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readTime}</span>
                    </div>

                    <h3 className="font-extrabold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-3 leading-snug">
                      <Link to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h3>

                    <p className="text-muted-foreground text-sm line-clamp-3 leading-relaxed">
                      {post.summary}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-border/60 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {post.tags.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className="text-[10px] bg-muted px-2 py-0.5 rounded-md text-muted-foreground font-medium">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-xs font-bold text-primary hover:underline shrink-0"
                    >
                      Devamını Oku <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-card rounded-2xl border border-border my-8 space-y-3">
            <p className="text-lg font-bold text-foreground">Aramanıza uygun rehber yazısı bulunamadı.</p>
            <p className="text-sm text-muted-foreground">Farklı bir anahtar kelime veya kategori seçebilirsiniz.</p>
            <Button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("Tümü");
              }}
              variant="outline"
              size="sm"
            >
              Filtreleri Temizle
            </Button>
          </div>
        )}

        {/* Load More Button */}
        {listPosts.length > visibleCount && (
          <div className="text-center mt-12 mb-8">
            <Button
              onClick={() => setVisibleCount((prev) => prev + 6)}
              size="lg"
              variant="outline"
              className="font-bold px-8 rounded-xl shadow-xs hover:bg-primary hover:text-primary-foreground transition-all"
            >
              Daha Fazla Rehber Yazısı Yükle ({listPosts.length - visibleCount} yazı kalıyor)
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </main>

      <FooterSEO />
    </div>
  );
}
